import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { CategoryKind, CategorySummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const ledgerCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/categories',
  component: LedgerCategoriesPage,
});

type FormValues = { name: string; kind: CategoryKind };

const KIND_ORDER: CategoryKind[] = ['income', 'expense', 'transfer', 'advance'];

function LedgerCategoriesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['ledger-categories'],
    queryFn: () => api.listCategories(),
  });

  const form = useForm<FormValues>({
    defaultValues: { name: '', kind: 'expense' },
  });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => api.createCategory(values),
    onSuccess: async () => {
      setDialogOpen(false);
      setErrorMessage(null);
      form.reset({ name: '', kind: 'expense' });
      await queryClient.invalidateQueries({ queryKey: ['ledger-categories'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateCategory(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ledger-categories'] });
    },
  });

  const grouped = React.useMemo(() => {
    const map = new Map<CategoryKind, CategorySummary[]>();
    for (const k of KIND_ORDER) map.set(k, []);
    for (const c of listQuery.data ?? []) {
      const bucket = map.get(c.kind);
      if (bucket) bucket.push(c);
    }
    return map;
  }, [listQuery.data]);

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Categories</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Categorize money movements. "Transfer" kind is reserved for the two sides of a transfer pair.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>New category</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New category</DialogTitle>
              <DialogDescription>Names must be unique across all kinds.</DialogDescription>
            </DialogHeader>
            <form
              onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col gap-1.5">
                <Label>Name</Label>
                <Input {...form.register('name', { required: true })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Kind</Label>
                <Select {...form.register('kind')}>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                  <option value="transfer">Transfer</option>
                  <option value="advance">Advance</option>
                </Select>
              </div>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving…' : 'Create category'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {KIND_ORDER.map((kind) => {
        const items = grouped.get(kind) ?? [];
        return (
          <Card key={kind}>
            <CardHeader>
              <CardTitle className="capitalize">{kind}</CardTitle>
              <CardDescription>{items.length} categor{items.length === 1 ? 'y' : 'ies'}</CardDescription>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">None yet.</p>
              ) : (
                <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
                  {items.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="font-medium">{c.name}</span>
                        {!c.active ? <Badge variant="destructive">inactive</Badge> : null}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!c.active || deactivateMutation.isPending}
                        onClick={() => deactivateMutation.mutate(c.id)}
                      >
                        {c.active ? 'Deactivate' : 'Inactive'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        );
      })}
    </AppShell>
  );
}
